import { InputAdornment, InputBase, InputBaseProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useFormat } from "@sinch/core/hooks/format";
import { isEmpty, isNil } from "ramda";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";

interface CurrencyInputProps extends Omit<InputBaseProps, "value" | "onChange" | "onBlur" | "readOnly"> {
  value: number | null;
  onChange: (val: number | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  adornment?: ReactNode;
  readOnly?: boolean;
}

/**
 * A customized input component for handling currency values.
 * Switches between formatted display mode and editable number mode.
 *
 */
export const CurrencyInput = ({ value, onChange, onBlur, adornment, readOnly, ...props }: CurrencyInputProps) => {
  const [editMode, setEditMode] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const { num } = useFormat();

  useEffect(() => {
    if (editMode && ref.current) {
      ref.current.select();
    }
  }, [editMode]);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onBlur?.(e);
      setEditMode(false);
    },
    [onBlur]
  );

  const handleChange = useCallback(
    (val: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (editMode) {
        onChange(isEmpty(val.target.value) ? null : Number.parseFloat(val.target.value));
      }
    },
    [editMode, onChange]
  );

  const handleFocus = useCallback(() => {
    if (readOnly) {
      return;
    }
    setEditMode(true);
  }, [readOnly]);

  const currencyValue = isNil(value) ? "" : num.currency(value);
  return (
    <CurrencyInputBase
      endAdornment={<InputAdornment position="end">{adornment}</InputAdornment>}
      fullWidth
      inputProps={{ ref: ref }}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      readOnly={readOnly}
      type={editMode ? "number" : "text"}
      value={editMode ? value : currencyValue}
      {...props}
    />
  );
};

const CurrencyInputBase = styled(InputBase)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  height: "100%",
  "& input": {
    textAlign: "right",
  },
}));
